import { poweredByHtml } from '@shared/branding/powered-by';

/**
 * POS-I240: the ONE builder of the printable popup documents (reports, statements, purchase
 * documents, delivery note, khata list). Pure string building; `openPrintWindow` in
 * print-document.ts opens it. Every document ends with the product's "Powered by" line, repeated
 * on every printed page (a table footer group, which reserves its own space so it never overlaps
 * the body, unlike a fixed element).
 */
export const escapeHtmlText = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/** Base style shared by every popup document. Class names of the old blobs are kept. */
export const PRINT_BASE_CSS = `
  body{font-family:system-ui,-apple-system,sans-serif;padding:24px;color:#1a1a2e;font-size:13px;}
  h1{font-size:20px;margin:0 0 4px;}
  .sub,.subtitle,.muted{font-size:12px;color:#666;margin-bottom:12px;}
  table{width:100%;border-collapse:collapse;}
  th,td{padding:6px 10px;text-align:left;border-bottom:1px solid #e5e7eb;}
  th{font-weight:600;background:#f9fafb;}
  .text-end,.r{text-align:right;}
  .mono,.font-mono{font-family:ui-monospace,SFMono-Regular,Consolas,monospace;}
  .total,.total-row,.tot td{border-top:2px solid #333;font-weight:700;}
  .section-title{font-size:14px;font-weight:600;margin:16px 0 8px;}
  table.pb-frame{border-collapse:collapse;width:100%;}
  table.pb-frame>tbody>tr>td,table.pb-frame>tfoot>tr>td{padding:0;border:0;background:none;}
  .pb-foot{padding-top:14px;}
  .pb-paper{display:none;}
  @media print{
    button{display:none;}
    body{padding:0;}
    @page{margin:12mm;}
    table.pb-frame>tfoot{display:table-footer-group;}
    table.pb-frame>thead{display:table-header-group;}
    .pb-screen{display:none;}
    .pb-paper{display:block;}
  }
`;

export interface PrintDocumentOptions {
  /** Window / document title; escaped here. */
  title: string;
  /** Optional heading (h1), escaped here. Pages with their own heading inside bodyHtml leave it out. */
  heading?: string;
  /** Optional "Generated on ..." style line under the heading, escaped here. */
  subtitle?: string;
  /** Already-built, already-escaped HTML of the document. */
  bodyHtml: string;
  /** Page-specific CSS appended after the base style. */
  extraCss?: string;
  /** Translated "Powered by" words (default English). */
  poweredByLabel?: string;
  /** false for tests / previews that must not print on load. */
  autoPrint?: boolean;
  dir?: 'ltr' | 'rtl';
}

export function buildPrintDocument(o: PrintDocumentOptions): string {
  const footer =
    `<div class="pb-screen">${poweredByHtml({ label: o.poweredByLabel })}</div>` +
    `<div class="pb-paper">${poweredByHtml({ label: o.poweredByLabel, paper: true })}</div>`;
  const head =
    (o.heading ? `<h1>${escapeHtmlText(o.heading)}</h1>` : '') +
    (o.subtitle ? `<div class="sub">${escapeHtmlText(o.subtitle)}</div>` : '');
  return `<!DOCTYPE html><html dir="${o.dir ?? 'ltr'}"><head><meta charset="utf-8"><title>${escapeHtmlText(o.title)}</title>
<style>${PRINT_BASE_CSS}${o.extraCss ?? ''}</style></head><body>
<table class="pb-frame"><tbody><tr><td>${head}${o.bodyHtml}</td></tr></tbody><tfoot><tr><td class="pb-foot">${footer}</td></tr></tfoot></table>
${o.autoPrint === false ? '' : '<script>window.print();<\/script>'}</body></html>`;
}
